import { useState, useEffect, useCallback } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { apiPost } from '../../utils/api';
import { Card } from '../Card';
import Login from './Login';
import Loading from '../Loading';
import { Roles } from 'src/shared/abilities';
import { useUrlParams } from 'src/utils/url';
import HomeScreen from './HomeScreen';

type ViewState = 'loading' | 'login' | 'verified' | 'error';

const VerifyInvitePage = () => {
  const { setStorage } = useAuth();
  const params = useUrlParams();
  const org_id = params.org_id;
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const [view, setView] = useState<ViewState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [role, setRole] = useState<Roles>(Roles.BASIC_USER);
  const isAdminOnboarding = role === Roles.ORG_ADMIN;

  const markVerified = useCallback(async (username: string, password: string) => {
    try {
      const params = { token, username, password };
      const response = await apiPost(`/api/verify-invite/complete`, params);
      const data = await response.json();
      if (response.ok && !data.error) {
        setStorage({
          user: data.user,
          organizations: data.organizations,
          orgMemberships: data.orgMemberships,
          isDemoSession: data.isDemoSession,
        });
        setView('verified');
        setTimeout(() => {
          let redirectUrl = `${window.location.origin}/${org_id}`
          if (role === Roles.ORG_ADMIN) {
            redirectUrl += '/general-settings';
          } else {
            redirectUrl += '/patient_login';
          }
          window.location.href = redirectUrl;
        }, 2000);
      } else {
        throw new Error('Failed to verify invite');
      }
    } catch (error) {
      setView('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to verify invite');
    }
  }, [token, setStorage, org_id, role]);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await apiPost('/api/verify-invite', { token });
        const data = await response.json();

        if (response.ok && data.valid ) {
          setRole(data.role as Roles);
          setView('login');
        } else {
          setView('error');
          setErrorMessage(data.error || 'Invalid invite link.');
        }
      } catch {
        setView('error');
        setErrorMessage('Failed to verify invite link.');
      }
    };

    validateToken();
  }, [token]);

  return (
    <HomeScreen content={
      <>
        <h2 className="text-3xl font-bold mb-3">Verify Your Account</h2>
        <p className="text-lg opacity-90">Sign In to Complete Your Verification.</p>
      </>
    }>
      {(() => {
        switch (view) {
          case 'loading':
            return (
              <Card className="max-w-md w-full p-10 text-center">
                <Loading message="Verifying Invite..." />
              </Card>
            );
          case 'verified':
            return (
              <Card className="max-w-md w-full p-10 text-center space-y-4">
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Thank You for Verifying</h2>
                <p className="text-gray-600">Your account has been verified successfully.</p>
                <p className="text-gray-600">Redirecting to your dashboard...</p>
              </Card>
            );
          case 'error':
            return (
              <Card className="max-w-md w-full p-10 text-center space-y-4">
                <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-900">Invite Link Invalid</h2>
                <p className="text-gray-600">{errorMessage}</p>
              </Card>
            );
          case 'login':
          default:
            return (
              <Card className="w-full max-w-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">Verify Your Account</h2>
                <Login
                  mode={isAdminOnboarding ? 'admin' : 'patient'}
                  orgId={org_id}
                  passwordOnly={isAdminOnboarding}
                  externalPasswordHandler={markVerified}
                  redirectUrl={`/${org_id}/verify-invite?completed=true&token=${token}`}
                />
              </Card>
            );
        }
      })()}

    </HomeScreen>
  );
};

export default VerifyInvitePage;
